import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { CyberpassButton } from "../Input";
import Modal from "../Modal";
import * as customerApi from "../api/customer";
import PhoneIcon from "../../public/phone.svg?react";
import MoneyIcon from "../../public/money.svg?react";
import CheckMarkThinIcon from "../../public/checkMarkThin.svg?react";
import OnlineOrderPaymentQrCode from "./OnlineOrderPaymentQrCode";
import { formatObject } from "../common/formatting";
import EyeIcon from "../../public/eye.svg?react";
import ClosedEyeIcon from "../../public/closedEye.svg?react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "../i18n";
import { selectCurrent, setOnlineOrder } from "../store/customer";
import { selectAccessToken } from "../store/auth";

const ActiveOnlineOrder = () => {
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const customer = useSelector(selectCurrent);
  const [showModal, setShowModal] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const dispatch = useDispatch();

  const orderStatusInfo = [
    {
      IconComponent: PhoneIcon,
      iconFillType: "fill",
      title: t("order.confirmation"),
    },
    {
      IconComponent: MoneyIcon,
      iconFillType: "fill",
      title: t("order.payment"),
    },
    {
      IconComponent: CheckMarkThinIcon,
      iconFillType: "stroke",
      title: t("order.success"),
    },
  ];

  useEffect(() => {
    if (customer == null) {
      return;
    }
    if (customer.activeOrder == null) {
      return;
    }

    if (customer.activeOrder.status != 1) {
      return;
    }
    setShowModal(true);
  }, [customer?.activeOrder]);

  const orderStatusComponent = () => (
    <div className="flex justify-between w-full">
      {orderStatusInfo.map(({ title, IconComponent, iconFillType }, idx) => {
        console.debug(
          `Order status: ${customer.activeOrder.status}, idx: ${idx}`,
        );
        const isActive = customer.activeOrder.status - 1 > idx;
        return (
          <>
            <div key={`${idx}-${title}`} className="flex flex-col items-center">
              <div
                className={`w-16 h-16 mx-4 p-2 rounded-full border border-4 flex items-center justify-center ${isActive ? "border-[#46AA3D]" : "border-white"}`}
              >
                <IconComponent
                  width={null}
                  height={null}
                  fill={
                    iconFillType === "fill"
                      ? isActive
                        ? "#46AA3D"
                        : "white"
                      : "none"
                  }
                  stroke={
                    iconFillType === "stroke"
                      ? isActive
                        ? "#46AA3D"
                        : "white"
                      : "none"
                  }
                  className="h-10"
                />
              </div>
              <h1
                className={`mt-4 max-w-24 text-center ${isActive ? "text-[#46AA3D]" : "text-white"}`}
              >
                {title}
              </h1>
            </div>
            {idx < orderStatusInfo.length - 1 && (
              <div className="h-24 flex items-center" key={`${idx}-2`}>
                <hr
                  className={`w-16 h-1 border-none ${isActive ? "bg-[#46AA3D]" : "bg-white"}`}
                />
              </div>
            )}
          </>
        );
      })}
    </div>
  );

  const InfoEntryComponent = ({ title, value }) => (
    <div
      className={`grid grid-cols-${showQrCode ? 1 : "2 grid-flow-col"} gap-2 p-4`}
    >
      <h1 className="font-semibold">{title}</h1>
      <h2 className={`text-gray-300 ${!showQrCode ? "text-end" : ""}`}>
        {value}
      </h2>
    </div>
  );

  return (
    customer != null &&
    customer.activeOrder != null && (
      <div>
        {customer.activeOrder.status < 4 && (
          <CyberpassButton
            Icon={MoneyIcon}
            onClick={() => setShowModal(!showModal)}
            style={"green"}
          >
            <p className="ml-4">{t("order.paymentInProgress")}</p>
          </CyberpassButton>
        )}
        <Modal
          title={t("order.statusInfo")}
          showModal={showModal}
          setShowModal={setShowModal}
          align="right"
          hideConfirmButton={true}
          titleAlign={"center"}
          content={
            <div className="flex flex-col h-full px-4">
              <div
                className={`h-full flex justify-between ${showQrCode ? "items-center px-12" : "flex-col"}`}
              >
                {!showQrCode && <span className="mt-8" />}
                <div className="flex justify-between shrink-0">
                  {showQrCode ? (
                    <div className="mb-8">
                      <OnlineOrderPaymentQrCode
                        onlineOrder={customer.activeOrder}
                        size="md"
                      />
                    </div>
                  ) : (
                    orderStatusComponent()
                  )}
                </div>
                {!showQrCode && <span className="mb-4" />}
                <div className={`${showQrCode ? "" : "my-8"}`}>
                  <InfoEntryComponent
                    title={t("order.minutesAmount")}
                    value={customer.activeOrder.minutesAmount}
                  />

                  <InfoEntryComponent
                    title={t("order.cost")}
                    value={formatObject(
                      customer.activeOrder.priceRub,
                      "priceRub",
                    )}
                  />

                  <InfoEntryComponent
                    title={t("order.phoneNumber")}
                    value={formatObject(customer.phone, "phone")}
                  />
                </div>
              </div>
              {showQrCode && (
                <>
                  {orderStatusComponent()}
                  <span className="mb-2" />
                </>
              )}
              <div className="flex flex-row justify-between mb-2">
                {customer.activeOrder.status < 4 ? (
                  <>
                    <CyberpassButton
                      Icon={showQrCode ? ClosedEyeIcon : EyeIcon}
                      iconSize="small"
                      onClick={() => setShowQrCode(!showQrCode)}
                    >
                      {showQrCode ? t("order.hideQr") : t("order.showQr")}
                    </CyberpassButton>

                    <CyberpassButton
                      style="danger"
                      Icon={XMarkIcon}
                      iconSize="small"
                      onClick={() => {
                        if (confirm(t("order.confirmCancel"))) {
                          toast.promise(
                            async () => {
                              await customerApi.cancelOnlineOrder(
                                accessToken,
                                customer.activeOrder.id,
                              );
                              setShowModal(false);
                              dispatch(
                                setOnlineOrder({
                                  customerId: customer.id,
                                  order: null,
                                }),
                              );
                            },
                            {
                              loading: t("order.cancelLoading"),
                              success: t("toast.orderCancelled"),
                              error: t("toast.orderCancelFailed"),
                            },
                          );
                        }
                      }}
                    >
                      {t("order.cancelOrder")}
                    </CyberpassButton>
                  </>
                ) : (
                  <CyberpassButton onClick={() => setShowModal(false)}>
                    {t("common.confirm")}
                  </CyberpassButton>
                )}
              </div>
            </div>
          }
        ></Modal>
      </div>
    )
  );
};

export default ActiveOnlineOrder;
